import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi.ts";
import {makeAutoObservable, reaction, runInAction} from "mobx";
import type {
    ColumnConfiguration,
    CustomerConfigurationKeys,
    CustomerConfigurationMap,
    DatabagConfiguration
} from "./DatabagConfigClasses";
import {ProfileKeyMap} from "./DatabagConfigClasses";
import type AuthStore from "../../authentication/AuthStore";
import type ProfileStore from "../ProfileStore.tsx";
import type {GridColDef} from "@mui/x-data-grid-premium";

export function toDate(value) {
    if (!value) return null;
    return new Date(value);
}

/**
 * Perfect example of store that can be replaced with TanStack Query or similar library.
 */
export class DatabagConfigStore {
    databagConfig: DatabagConfiguration | null = null;

    customerConfig: CustomerConfigurationMap | null = null;

    _configurations: Map<number, DatabagConfiguration> = new Map();

    constructor(
        private api: MithraMaterializedApi,
        private auth: AuthStore,
        private profileStore: ProfileStore,
    ) {
        makeAutoObservable(this);
        reaction(() => this.auth.user, user => {
            if (!user) return;
            this.api.getCustomerConfiguration()
                .then(response => runInAction(() => this.customerConfig = new Map(response.data.map(c => [c.key, c]))))
                .catch(() => runInAction(() => this.customerConfig = new Map())) // TODO: What if we cannot login?
        }, {fireImmediately: true});
    }

    setDatabagConfiguration(databagId: number) {
        const conf = this._configurations.get(databagId);
        if (conf) {
            this.databagConfig = conf;
        } else {
            this.api.getDatabagConfiguration(databagId)
                .then(resp => this._setConfig(resp.data))
                .catch(err => runInAction(() => {
                    console.error(`Failed to load configuration for databag ${databagId}`, err);
                    this.databagConfig = {
                        // Fallback to empty configuration
                        databag_id: databagId,
                        columns: [],
                    };
                }))
            ;
        }
    }

    _setConfig(config: DatabagConfiguration) {
        this.databagConfig = config;
        this._configurations.set(config.databag_id, config);
    }

    getConfigKey(key: CustomerConfigurationKeys) {
        if (this.customerConfig && this.customerConfig.has(key))
            return this.customerConfig!.get(key)!.value;
        if (!ProfileKeyMap[key])
            throw new Error('No configuration found for key: ' + String(key));
        const valOrDefault = ProfileKeyMap[key]!.pKey ? (
            this.profileStore.p[ProfileKeyMap[key]!.pKey] ?? ProfileKeyMap[key]!.default) : ProfileKeyMap[key]!.default;
        return ProfileKeyMap[key]!.operation ? ProfileKeyMap[key]!.operation(valOrDefault) : valOrDefault;
    }

    getPartialGridColDef(column_name: string, default_header_name: string): Partial<GridColDef> {
        // Note that whether column has in_use flag active shouldn't matter here
        const colConfig: ColumnConfiguration | undefined = (this.databagConfig?.columns ?? []).find(c => c.column_name === column_name);
        const result: Partial<GridColDef> = {
            headerName: colConfig?.display_name || default_header_name,
        }
        // Configuration from old-style profile
        if (column_name === 'p_name' && this.profileStore.p.p_name_col_width) result['width'] = this.profileStore.p.p_name_col_width;
        if (column_name === 'p_name' && this.profileStore.p.p_name_col_min_width) result['minWidth'] = this.profileStore.p.p_name_col_min_width;
        if (column_name === 'p_ddate_1' && this.profileStore.p.p_ddate_1_col_width) result['width'] = this.profileStore.p.p_ddate_1_col_width;
        if (column_name === 's_name' && this.profileStore.p.s_name_col_width) result['width'] = this.profileStore.p.s_name_col_width;
        if (column_name === 'bu_name' && this.profileStore.p.p_bu_name_col_width) result['width'] = this.profileStore.p.p_bu_name_col_width;
        if (column_name === 'cat' && this.profileStore.p.cat_col_width) result['width'] = this.profileStore.p.cat_col_width;
        if (column_name === 'previous_cat' && this.profileStore.p.previous_cat_col_width) result['width'] = this.profileStore.p.previous_cat_col_width;
        // Configuration from hard-coded specs
        if (column_name === 'score' && !result['width']) result['width'] = 50;
        if (column_name === 'state' && !result['width']) result['width'] = 120;
        if (column_name === 'state' && !result['minWidth']) result['minWidth'] = 120;
        if (column_name === 'p_spend' && !result['width']) result['width'] = 90;
        // Configuration from API, overrides previous config if specified
        if (colConfig?.width) result['width'] = colConfig?.width;
        if (colConfig?.min_width) result['minWidth'] = colConfig.min_width;
        if (colConfig?.max_width) result['maxWidth'] = colConfig.max_width;
        // Maybe this should be moved to the API
        if (colConfig?.column_name.includes('ddate')) {
            // Automatically parse the ddate
            result.type = 'date';
            result.valueGetter = toDate
        }
        return result;
    }

    getDefaultOrder(column_name: string): number | null {
        // Two special cases are cat, previous_cat both of them should be appended with `_id`.
        // The reason for this is within the structure of ColumnConfiguration, where it expects to calculate states
        //  for a db_column, but in API we use a serializer for those fields to return actual objects and render them
        //  in one cell
        if (['cat', 'previous_cat'].includes(column_name)) column_name += '_id';
        return (this.databagConfig?.columns ?? []).find(c => c.column_name === column_name)?.default_order ??
            null;
    }
}
